import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Download, Lock, PenLine, Printer, Share2 } from 'lucide-react'
import { useStore } from '../data/store'
import { useI18n } from '../i18n'
import {
  buildInvoiceDoc, buildQuoteDoc, documentFileName, type DocLine, type DocModel,
} from '../lib/documents'
import { docLineLabel, docT } from '../locales/documents'
import { Button, Card, EmptyState } from '../ui/kit'
import { supabase } from '../lib/supabase'
import { useDocScale } from '../ui/useDocScale'
import { usePrintFileName } from '../ui/usePrintFileName'
import { dateiSichern, sichernTeilt } from '../lib/dateiSichern'
import { Belegblatt } from './Belegblatt'
import QuoteLinkPanel from './QuoteLinkPanel'

/**
 * Angebot und Rechnung als A4-Blatt — und der Weg, wie beides das Haus verlässt.
 *
 * Zwei Wege stehen nebeneinander, und das ist Absicht:
 *
 *   · „Drucken" (`window.print()`) ist der alte. Er bleibt, weil er nichts
 *     kostet und der Notausgang ist, wenn am neuen etwas klemmt.
 *   · „PDF sichern" erzeugt die Datei selbst (`lib/belegPdf.ts`) und reicht sie
 *     hinaus (`lib/dateiSichern.ts`). Am Schreibtisch spart das zwei von drei
 *     Handgriffen; in der iOS-Hülle ersetzt es einen Knopf, der dort NICHTS tut.
 *
 * Zu diesem „nichts": am 01.08.2026 im Simulator gemessen — auf „PDF" tippen
 * öffnet in der Hülle keinen Dialog, erzeugt keine Datei und meldet nichts. Ein
 * Vermieter mit einem iPhone konnte bis dahin überhaupt kein PDF erzeugen.
 */
export default function DocumentView() {
  const { kind, id } = useParams<{ kind: string; id: string }>()
  const navigate = useNavigate()
  const store = useStore()
  const { t } = useI18n()
  const { viewportRef, sheetRef } = useDocScale<HTMLDivElement, HTMLElement>([])
  const [issuing, setIssuing] = useState(false)
  /**
   * The RKSV receipt belonging to this invoice, if the register is running. Fetched
   * here rather than kept in the store: it exists for a minority of tenants and is
   * written by an Edge Function, so the document is the only place that needs it.
   */
  const [rksvQr, setRksvQr] = useState<string | null>(null)

  // A draft invoice is the only document that can still be changed, and the only one
  // that carries no number. Both facts have to be visible before it is printed.
  const invoice = kind === 'invoice' ? store.db.invoices.find((i) => i.id === id) : undefined
  const isDraftInvoice = Boolean(invoice && !invoice.issued_at)

  useEffect(() => {
    let alive = true
    setRksvQr(null)
    if (kind !== 'invoice' || !id || !store.live || !supabase) return
    void supabase.from('rksv_receipts')
      .select('qr_payload, signature_status').eq('invoice_id', id).maybeSingle()
      .then(({ data }) => {
        if (alive && data?.qr_payload) setRksvQr(String(data.qr_payload))
      })
    return () => { alive = false }
  }, [kind, id, store.live])

  const model = useMemo<DocModel | null>(() => {
    if (!id) return null
    const tenant = store.db.tenant

    if (kind === 'invoice') {
      const invoice = store.db.invoices.find((i) => i.id === id)
      if (!invoice) return null
      const items = store.db.invoice_items.filter((it) => it.invoice_id === invoice.id)
      const booking = store.db.bookings.find((b) => b.id === invoice.booking_id)
      const unit = booking ? store.unitById(booking.unit_id) : undefined
      return buildInvoiceDoc(invoice, items, {
        tenant,
        guest: store.guestById(invoice.guest_id) ?? undefined,
        booking,
        unit,
        property: store.db.properties.find((p) => p.id === unit?.property_id),
        paid: invoice.booking_id ? store.paidFor(invoice.booking_id) : invoice.paid_amount,
        // Getrennt, damit die Rechnung sie benennen kann statt sie unter
        // „bereits bezahlt" verschwinden zu lassen.
        prepaid: invoice.booking_id
          ? store.db.payments
            .filter((p) => p.booking_id === invoice.booking_id && p.kind === 'prepayment')
            .reduce((a, p) => a + p.amount, 0)
          : 0,
        // Die jüngste Zahlung bestimmt, was auf dem Beleg als Quittungszeile steht.
        ...(() => {
          const last = invoice.booking_id
            ? [...store.db.payments]
              .filter((p) => p.booking_id === invoice.booking_id
                && p.kind !== 'security_deposit' && p.kind !== 'security_deposit_refund')
              .sort((a, b) => a.received_on.localeCompare(b.received_on))
              .at(-1)
            : undefined
          return last ? { paidOn: last.received_on, paidMethod: last.method } : {}
        })(),
      })
    }

    if (kind === 'quote') {
      const quote = store.db.quotes.find((q) => q.id === id)
      if (!quote) return null
      const booking = store.db.bookings.find((b) => b.id === quote.booking_id)
      const unit = booking ? store.unitById(booking.unit_id) : undefined

      // Prefer the snapshot written when the quote was created; fall back to a fresh
      // calculation for quotes made before snapshots existed.
      const snapshot = (quote.snapshot?.lines as DocLine[] | undefined) ?? null
      const lang = quote.language === 'en' ? 'en' : 'de'
      const lines: DocLine[] = snapshot ?? (booking
        ? (store.price(booking)?.lines ?? []).map((l) => ({
            label: l.nameJson?.[lang] ?? l.nameJson?.de ?? docLineLabel(lang, l.code),
            qty: l.qty, unitPrice: l.unitPrice, net: l.net,
            vatRate: l.vatRate, vat: l.vat, gross: l.gross,
          }))
        : [])

      return buildQuoteDoc(quote, lines, {
        tenant,
        guest: store.guestById(booking?.guest_id ?? null) ?? undefined,
        booking,
        unit,
        property: store.db.properties.find((p) => p.id === unit?.property_id),
      })
    }
    return null
  }, [kind, id, store, t])

  // Der Dateiname beim Drucken — der Browser nimmt dafür den Seitentitel.
  usePrintFileName(model ? documentFileName(model) : null)

  /*
   * Die Datei entsteht, SOBALD der Beleg dasteht — nicht erst beim Druck auf den
   * Knopf.
   *
   * WARUM DIE REIHENFOLGE TRÄGT: das Teilen-Blatt verlangt eine unmittelbare
   * Nutzergeste. Wer im Klickhandler erst eine Sekunde lang ein PDF baut, hat
   * sie verloren, bis er fertig ist — und dann passiert beim Tippen einfach
   * nichts. Kein Dialog, keine Meldung, das Fehlerbild „der Knopf ist kaputt".
   * Also: Blatt auf → im Hintergrund liegt die Datei fertig → erst dann wird der
   * Knopf gedrückt.
   *
   * `await import` und nicht oben importiert: pdf-lib und die beiden Schriften
   * wiegen zusammen mehrere hundert Kilobyte. Wer nur einen Beleg anschaut, soll
   * sie nicht mitladen — und das Hauptbündel darf um kein Byte wachsen.
   */
  const [datei, setDatei] = useState<{ name: string; bytes: Uint8Array } | null>(null)
  const [pdfFehler, setPdfFehler] = useState<{ code: string; zeichen?: string } | null>(null)
  const [fehlerSichtbar, setFehlerSichtbar] = useState(false)
  const [sichert, setSichert] = useState(false)

  useEffect(() => {
    let alive = true
    setDatei(null)
    setPdfFehler(null)
    setFehlerSichtbar(false)
    if (!model) return
    void (async () => {
      try {
        const { belegPdf, belegDateiname } = await import('../lib/belegPdf')
        const bytes = await belegPdf({ model, rksvQr })
        if (alive) setDatei({ name: belegDateiname(model), bytes })
      } catch (fehler) {
        if (!alive) return
        const f = fehler as { code?: string; zeichen?: string }
        setPdfFehler({ code: f.code ?? 'unbekannt', zeichen: f.zeichen })
      }
    })()
    return () => { alive = false }
  }, [model, rksvQr])

  if (!model) {
    return (
      <div className="p-4">
        <Card><EmptyState title={t('common.noResults')} /></Card>
      </div>
    )
  }

  const quote = kind === 'quote' ? store.db.quotes.find((q) => q.id === id) : undefined

  // Die Buchung hinter dem Beleg — für den Sprung zurück in die Bearbeitung.
  const bookingId = kind === 'invoice'
    ? invoice?.booking_id ?? null
    : store.db.quotes.find((q) => q.id === id)?.booking_id ?? null

  const title = docT(model.lang, model.kind)

  return (
    <div className="min-h-full">
      {/* Toolbar — hidden when printing */}
      <div className="no-print sticky top-0 z-10 bg-surface border-b border-border
                      px-4 py-3 flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft size={18} />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate">{title} {model.number}</div>
          <div className="text-xs text-muted">{model.recipient.name}</div>
        </div>
        {/* Zurück in die Bearbeitung. Wer eine Rechnung anschaut, sieht meistens
            genau dort, dass an der Buchung etwas nicht stimmt — und musste sie
            bisher über den Kalender wiederfinden. */}
        {bookingId && (
          <Button variant="ghost" size="sm"
            onClick={() => navigate(`/bookings?buchung=${bookingId}`)}>
            <PenLine size={16} />
            <span className="hidden sm:inline">{t('booking.title')}</span>
          </Button>
        )}
        {isDraftInvoice && (
          <Button variant="primary" size="sm" disabled={issuing}
            onClick={async () => { setIssuing(true); await store.issueInvoice(invoice!.id); setIssuing(false) }}>
            <Lock size={16} /> {t('invoice.issue')}
          </Button>
        )}
        {/* Der neue Weg. Der Knopf erscheint erst, wenn die Datei fertig ist —
            oder wenn feststeht, dass keine entstehen kann; dann sagt er es. */}
        <Button variant={isDraftInvoice ? 'secondary' : 'primary'} size="sm"
          disabled={(!datei && !pdfFehler) || sichert}
          onClick={async () => {
            if (!datei) { setFehlerSichtbar(true); return }
            setSichert(true)
            try {
              await dateiSichern(datei)
            } catch {
              // Ein Abbruch im Teilen-Blatt ist kein Fehler und kommt hier nie
              // an. Was hier ankommt, ist ein voller Sandkasten oder ein Ziel,
              // das die Datei nicht annimmt — und das muss der Vermieter sehen,
              // sonst hält er einen misslungenen Griff für einen gelungenen.
              setPdfFehler({ code: 'sichern_fehlgeschlagen' })
              setFehlerSichtbar(true)
            } finally {
              setSichert(false)
            }
          }}>
          {sichernTeilt() ? <Share2 size={16} /> : <Download size={16} />}
          <span className="hidden sm:inline">
            {!datei && !pdfFehler
              ? t('document.preparing')
              : sichernTeilt() ? t('document.sharePdf') : t('document.savePdf')}
          </span>
        </Button>
        {/* Der alte Weg bleibt: er kostet nichts und ist der Notausgang, wenn am
            neuen etwas klemmt. In der Hülle tut er nichts — dort steht der
            Knopf darüber. */}
        <Button variant="ghost" size="sm" onClick={() => window.print()}>
          <Printer size={16} />
          <span className="hidden sm:inline">{t('document.print')}</span>
        </Button>
      </div>

      {fehlerSichtbar && pdfFehler && (
        <div className="no-print px-4 py-2 bg-warn-soft text-warn text-sm">
          {pdfFehler.code === 'entwurf_ohne_nummer'
            ? t('document.draftBlocked')
            : pdfFehler.code === 'zeichen_nicht_darstellbar'
              ? t('document.charsetBlocked', { z: pdfFehler.zeichen ?? '' })
              : pdfFehler.code === 'sichern_fehlgeschlagen'
                ? t('document.saveFailed')
                : t('document.pdfFailed')}
        </div>
      )}

      {isDraftInvoice && (
        <div className="no-print px-4 py-2 bg-warn-soft text-warn text-sm">
          {t('invoice.draftHint')}
        </div>
      )}

      {/*
        Wie der Beleg zum Gast kommt. StaySuite verschickt ihn nicht — der Satz steht
        hier, wo der Vermieter ihn braucht, und nicht in einer Meldung, die erst nach
        einem Druck auf einen toten Knopf erscheint.

        Beim Entwurf steht er NICHT: eine Rechnung ohne Nummer soll nicht hinausgehen,
        und eine Aufforderung zum Verschicken direkt unter der Entwurfswarnung wäre
        eine Einladung dazu.
      */}
      {!isDraftInvoice && (
        <p className="no-print px-4 pt-2 text-xs text-muted">{t('mail.ownProgram')}</p>
      )}

      {/* Der Weg zur Online-Fassung. Er gehört an den Beleg und nicht in eine Liste:
          hier steht der Vermieter, wenn er das Angebot verschicken will. */}
      {quote && <QuoteLinkPanel quote={quote} />}

      <div className="p-4 no-print-padding doc-viewport" ref={viewportRef}>
        <Belegblatt model={model} rksvQr={rksvQr} blattRef={sheetRef} />
      </div>
    </div>
  )
}
